/**
 * `2d` — Discovery de oportunidades.
 *
 * O feed automático: Compute Optimizer, Cost Optimization Hub, o Parquet do
 * CUDOS (batch diário) e a IA sob demanda. O arquiteto seleciona linhas e as
 * envia para a gestão como `rascunho`.
 *
 * 🔒 **BR-25 — a geração por IA é SEMPRE por clique.** É `useMutation` e nunca
 * `useQuery`, e não existe nenhum `useEffect` nesta tela que a dispare. Uma
 * query rodaria sozinha ao montar, que é exatamente o que a regra proíbe: a
 * chamada custa dinheiro por execução.
 *
 * 🔒 **BR-02 — nada daqui é publicado automaticamente.** A promoção cria
 * oportunidades em `rascunho`, invisíveis ao Cliente. Isso é garantido pelo
 * servidor: o corpo do POST nem aceita `status` ou `visibilidadeCliente`.
 */
import { useState } from 'react'

import {
  useDescartarDiscovery,
  useDiscovery,
  useGerarRecomendacoesIa,
  usePromoverDiscovery,
} from '@/api/queries/finops'
import { mensagemDoErro } from '@/components/erros'
import { EsqueletoDeTabela } from '@/components/esqueleto'
import { EstadoDeErro, EstadoVazio } from '@/components/estados'
import { Botao } from '@/components/primitivos'
import { formatarUsd } from '@/format'
import { textoDoResultadoDaIa } from '@/modules/finops/textos'
import { useClienteAtual } from '@/tenant/useClienteAtual'

const ICONE_DA_ORIGEM: Record<string, string> = {
  'compute-optimizer': 'robot',
  'cost-optimization-hub': 'robot',
  'cudos-parquet': 'database',
  ia: 'sparkle',
}

export function Discovery() {
  const idCliente = useClienteAtual()
  const [origem, setOrigem] = useState<string | null>(null)
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set())

  const consulta = useDiscovery(idCliente, origem ? { origem: [origem] } : {})
  const promover = usePromoverDiscovery(idCliente)
  const descartar = useDescartarDiscovery(idCliente)
  const gerarIa = useGerarRecomendacoesIa(idCliente)

  const itens = consulta.data?.itens ?? []
  const podePromover = consulta.data?.podePromover ?? false

  const alternar = (id: string) =>
    setSelecionadas((atual) => {
      const proximo = new Set(atual)
      if (proximo.has(id)) proximo.delete(id)
      else proximo.add(id)
      return proximo
    })

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Chips COM CONTAGEM, calculada pelo servidor ANTES do filtro de
              origem — senão o chip não selecionado mostraria "(0)" e o usuário
              concluiria que aquela fonte não trouxe nada. */}
          {(consulta.data?.contagemPorOrigem ?? []).map((chip) => {
            const ativo = chip.origem === 'todas' ? origem === null : origem === chip.origem
            return (
              <button
                key={chip.origem}
                type="button"
                aria-pressed={ativo}
                onClick={() => setOrigem(chip.origem === 'todas' ? null : chip.origem)}
                className={`rounded-control px-3 py-1.5 type-sm duration-fast ${
                  ativo
                    ? 'bg-iox-black text-text-inverse'
                    : 'border-border-subtle text-text-body border hover:border-border-strong'
                }`}
              >
                {chip.rotulo} ({chip.total})
              </button>
            )
          })}
        </div>

        {podePromover && (
          <Botao
            variante="secundaria"
            icone="sparkle"
            disabled={gerarIa.isPending}
            // 🔒 BR-25: SÓ no clique. Nunca em efeito de montagem.
            onClick={() => gerarIa.mutate()}
          >
            {gerarIa.isPending ? 'gerando…' : 'gerar recomendações da IA'}
          </Botao>
        )}
      </div>

      {gerarIa.data && (
        <p
          role="status"
          className="rounded-field bg-success-100 text-success-700 px-4 py-3 type-sm"
        >
          {textoDoResultadoDaIa(gerarIa.data.quantidadeSugestoes)}
        </p>
      )}

      {gerarIa.isError && (
        <p
          role="status"
          className="rounded-field bg-warning-100 text-warning-700 px-4 py-3 type-sm"
        >
          {mensagemDoErro(gerarIa.error)}
        </p>
      )}

      {consulta.isPending && <EsqueletoDeTabela rotulo="Carregando recomendações…" colunas={6} />}
      {consulta.isError && (
        <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
      )}

      {consulta.data && itens.length === 0 && (
        <EstadoVazio
          titulo="Nenhuma recomendação nova nesta origem."
          descricao="O inventário é atualizado por batch diário (Compute Optimizer, Cost Optimization Hub e o Parquet do CUDOS). A IA roda sob demanda, pelo botão acima."
        />
      )}

      {itens.length > 0 && (
        <div className="border-border-subtle bg-surface-card rounded-card overflow-x-auto border">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-border-subtle border-b">
                <th scope="col" className="w-10 px-3 py-3">
                  <span className="sr-only">Selecionar</span>
                </th>
                {['Recomendação', 'Origem', 'Conta', 'Serviço'].map((rotulo) => (
                  <th
                    key={rotulo}
                    scope="col"
                    className="text-text-muted px-3 py-3 text-left type-eyebrow"
                  >
                    {rotulo}
                  </th>
                ))}
                <th
                  scope="col"
                  className="text-text-muted px-3 py-3 text-right type-eyebrow whitespace-nowrap"
                >
                  Economia est./mês
                </th>
              </tr>
            </thead>
            <tbody>
              {itens.map((linha) => {
                const marcada = selecionadas.has(linha.idDiscovery)
                return (
                  <tr
                    key={linha.idDiscovery}
                    className={`border-border-subtle border-b last:border-b-0 ${
                      marcada ? 'bg-navy-50' : ''
                    }`}
                  >
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={marcada}
                        disabled={!podePromover}
                        aria-label={`Selecionar ${linha.recomendacao}`}
                        onChange={() => alternar(linha.idDiscovery)}
                      />
                    </td>
                    <td className="text-text-primary px-3 py-3 type-sm font-semibold">
                      {linha.recomendacao}
                    </td>
                    <td className="text-text-body px-3 py-3 type-sm whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">
                        <i
                          className={`ph ph-${ICONE_DA_ORIGEM[linha.origemFerramenta] ?? 'circle'} text-icon-default`}
                          aria-hidden="true"
                        />
                        {linha.rotuloOrigem ?? linha.origemFerramenta}
                      </span>
                    </td>
                    <td className="text-text-body px-3 py-3 type-sm whitespace-nowrap">
                      {linha.accountId ?? '—'}
                    </td>
                    <td className="text-text-body px-3 py-3 type-sm whitespace-nowrap">
                      {linha.servicoAws ?? '—'}
                    </td>
                    <td className="text-text-primary px-3 py-3 text-right type-sm font-semibold whitespace-nowrap">
                      {linha.economiaEstimadaMensal === null
                        ? '—'
                        : formatarUsd(linha.economiaEstimadaMensal)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {podePromover && selecionadas.size > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <Botao
            variante="primaria"
            disabled={promover.isPending}
            onClick={() =>
              promover.mutate([...selecionadas], { onSuccess: () => setSelecionadas(new Set()) })
            }
          >
            {promover.isPending
              ? 'enviando…'
              : `enviar ${selecionadas.size} selecionada${selecionadas.size > 1 ? 's' : ''} para oportunidades`}
          </Botao>
          <Botao
            variante="secundaria"
            disabled={descartar.isPending}
            onClick={() =>
              descartar.mutate([...selecionadas], { onSuccess: () => setSelecionadas(new Set()) })
            }
          >
            descartar selecionadas
          </Botao>
          <span className="text-text-muted type-sm">
            Entram na gestão com status <strong>rascunho</strong>, pré-preenchidas com o que a
            origem forneceu. O arquiteto completa os custos.
          </span>
        </div>
      )}

      {/* Resultado PARCIAL: promover 20 e falhar em 1 não desfaz as 19, e o
          usuário precisa saber exatamente o que aconteceu com cada uma. */}
      {promover.data && (
        <p
          role="status"
          className="rounded-field bg-success-100 text-success-700 px-4 py-3 type-sm"
        >
          {promover.data.criadas.length} oportunidade
          {promover.data.criadas.length === 1 ? '' : 's'} criada
          {promover.data.criadas.length === 1 ? '' : 's'} em rascunho
          {promover.data.criadas.length > 0 &&
            ` (${promover.data.criadas.map((c) => c.codigo).join(', ')})`}
          {promover.data.ignoradas.length > 0 &&
            ` · ${promover.data.ignoradas.length} ignorada${promover.data.ignoradas.length === 1 ? '' : 's'}: ${promover.data.ignoradas
              .map((i) => (i.motivo === 'JA_PROMOVIDA' ? 'já promovida' : 'não encontrada'))
              .join(', ')}`}
          .
        </p>
      )}

      {promover.isError && <EstadoDeErro erro={promover.error} />}
    </section>
  )
}
